import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { 
  LayoutDashboard, 
  Users, 
  ClipboardList, 
  Dumbbell, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  Globe,
  CreditCard,
  Lightbulb,
  BookOpen,
  Activity,
  GraduationCap,
  Apple,
  Shield,
  Link2,
  Zap,
  HeartPulse,
  Wrench,
  ShieldCheck,
  Layers
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { logout } = useAuth();
  const { t, language, changeLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'dashboard' },
    { path: '/admin/clients', icon: Users, label: 'clients' },
    { path: '/admin/plans', icon: ClipboardList, label: 'plans' },
    { path: '/admin/macros', icon: Layers, label: 'Macros' },
    { path: '/admin/exercises', icon: Dumbbell, label: 'exercises' },
    { path: '/admin/library-reset', icon: ShieldCheck, label: 'Reset Librería v2' },
    { path: '/admin/automation', icon: Zap, label: 'Reglas Guardián' },
    { path: '/admin/guardian', icon: Shield, label: 'Objetivo Guardián' },
    { path: '/admin/payments', icon: CreditCard, label: 'Pagos' },
    { path: '/admin/tips', icon: Lightbulb, label: 'Píldoras del Día' },
    { path: '/admin/pildoras', icon: GraduationCap, label: 'Formación' },
    { path: '/admin/nutricion', icon: Apple, label: 'Nutrición 🔒' },
    { path: '/admin/recursos', icon: BookOpen, label: 'Centro de Recursos' },
    { path: '/admin/access-control', icon: Activity, label: 'Control de Accesos' },
    { path: '/admin/trainers', icon: Users, label: 'Entrenadores' },
    { path: '/admin/system-reset', icon: Wrench, label: 'Reset Producción' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden glass-header fixed top-0 left-0 right-0 z-50 h-16 flex items-center justify-between px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(true)}
          data-testid="mobile-menu-button"
        >
          <Menu className="h-6 w-6" />
        </Button>
        <img 
          src="https://customer-assets.emergentagent.com/job_plan-tracker-25/artifacts/7nvt53w6_Sin%20t%C3%ADtulo.jpeg" 
          alt="TRINES PORTAL 33 - FIT GUARDIAN" 
          className="h-10"
        />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}
          data-testid="mobile-language-toggle"
        >
          <Globe className="h-5 w-5" />
        </Button>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/80 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-screen w-64 sidebar transform transition-transform duration-300
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-20 flex items-center justify-between px-6 border-b border-white/10">
            <img 
              src="https://customer-assets.emergentagent.com/job_plan-tracker-25/artifacts/7nvt53w6_Sin%20t%C3%ADtulo.jpeg" 
              alt="TRINES PORTAL 33 - FIT GUARDIAN" 
              className="h-12 logo-container"
              data-testid="sidebar-logo"
            />
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-6">
            <nav className="space-y-1 px-3">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-all
                    ${isActive(item.path) 
                      ? 'nav-active text-primary bg-primary/10' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                    }
                  `}
                  data-testid={`nav-${item.label}`}
                >
                  <item.icon className="h-5 w-5" strokeWidth={1.5} />
                  {t(item.label)}
                </Link>
              ))}
            </nav>
          </ScrollArea>

          {/* Footer */}
          <div className="p-4 border-t border-white/10 space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-foreground"
              onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}
              data-testid="sidebar-language-toggle"
            >
              <Globe className="h-5 w-5 mr-3" strokeWidth={1.5} />
              {language === 'es' ? 'English' : 'Español'}
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-destructive"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-5 w-5 mr-3" strokeWidth={1.5} />
              {t('logout')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0">
        <div className="min-h-screen p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
